/** Open invitation links of a workspace, with a Revoke action. Admin-only on the server. */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRevokeInvite, useWorkspaceInvites } from "../../hooks/useWorkspaceMembers";
import { callableErrorCode } from "../../lib/invite";

export function PendingInvitations({ workspaceId }: { workspaceId: string }) {
  const { t, i18n } = useTranslation();
  const { data: invites = [], error } = useWorkspaceInvites(workspaceId);
  const revoke = useRevokeInvite(workspaceId);
  const [revokeError, setRevokeError] = useState(false);

  // A non-admin is refused the list: they have no invitations to manage, so show nothing.
  const denied = callableErrorCode(error) === "permission-denied";
  if (denied || (!error && invites.length === 0)) return null;

  const handleRevoke = (inviteId: string) => {
    setRevokeError(false);
    revoke.mutate(inviteId, {
      onError: (err) => {
        console.error("revokeInvite failed", callableErrorCode(err));
        setRevokeError(true);
      },
    });
  };

  return (
    <div className="mb-6 bg-card rounded-lg border border-border">
      <h2 className="px-4 py-3 text-sm font-medium text-foreground border-b border-border">{t("invite.pending.title")}</h2>
      {error ? (
        <p className="px-4 py-3 text-sm text-red-600">{t("invite.pending.loadFailed")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {invites.map((invite) => (
            <li key={invite.inviteId} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{invite.inviteeName || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {[invite.inviteeEmail || invite.inviteePhone, t(`workspaceMembers.roles.${invite.role}`), t("invite.pending.expires", { date: new Date(invite.expiresAt).toLocaleDateString(i18n.language) })]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(invite.inviteId)}
                disabled={revoke.isPending}
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
              >
                {t("invite.pending.revoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
      {revokeError && <p className="px-4 pb-3 text-sm text-red-600">{t("invite.pending.revokeFailed")}</p>}
    </div>
  );
}
